import { PLAN_ALLOWANCE_MINUTES, PLAN_PRICE_MINOR, type Currency, type PlanTier } from '@acs/shared';
import type { TranslationKey } from '../../i18n/index.js';

/**
 * The three published plans, as data (doc 23).
 *
 * ## Where the money actually comes from
 *
 * These amounts are for DISPLAY ONLY. What the owner is charged is the Stripe
 * price the server picks from the tier and the business's currency
 * (`priceIdFor`), and a client never sends a price id — otherwise the person
 * paying would be choosing what to pay. So a mismatch between the number on
 * this card and the number on the Stripe page is a display bug, not an
 * overcharge, but it is still the worst kind of display bug.
 *
 * Which is why the numbers are no longer written here. They come from
 * `PLAN_PRICE_MINOR` in `@acs/shared`, the same copy `npm run verify:stripe`
 * compares against the live Stripe price objects — so "keep the card in step
 * with the dashboard" is a check that runs rather than a note someone reads.
 */
export interface PlanCard {
  tier: PlanTier;
  nameKey: TranslationKey;
  descKey: TranslationKey;
  /**
   * List price in minor units, per currency.
   *
   * From `PLAN_PRICE_MINOR` (doc 24 §2.1), never typed in here: `verifyStripe`
   * checks that table against the live Stripe prices, and a local copy would be
   * the one number in the product that nothing verifies.
   */
  amount: Record<Currency, number>;
  /**
   * Conversation minutes included per billing period.
   *
   * Read from `PLAN_ALLOWANCE_MINUTES` in `@acs/shared` rather than repeated
   * here (doc 26 D9): the gateway ENFORCES this number and the warning SMS is
   * measured against it, so a display copy that drifted would advertise an
   * allowance different from the one that silences the phone.
   */
  includedMinutes: number;
  /** The one carrying the "most popular" ribbon and the primary button. */
  featured?: boolean;
}

export const PLAN_CARDS: PlanCard[] = [
  {
    tier: 'starter',
    nameKey: 'billing.plan.starter.name',
    descKey: 'billing.plan.starter.desc',
    amount: PLAN_PRICE_MINOR.starter,
    includedMinutes: PLAN_ALLOWANCE_MINUTES.starter,
  },
  {
    tier: 'pro',
    nameKey: 'billing.plan.pro.name',
    descKey: 'billing.plan.pro.desc',
    amount: PLAN_PRICE_MINOR.pro,
    includedMinutes: PLAN_ALLOWANCE_MINUTES.pro,
    featured: true,
  },
  {
    tier: 'business',
    nameKey: 'billing.plan.business.name',
    descKey: 'billing.plan.business.desc',
    amount: PLAN_PRICE_MINOR.business,
    includedMinutes: PLAN_ALLOWANCE_MINUTES.business,
  },
];

/**
 * Format a plan amount in the reader's locale and the business's currency.
 *
 * Two different axes on purpose, and the same split doc 20 D5 draws elsewhere:
 * the LOCALE decides the separators and where the symbol sits (`29,00 €` in
 * French, `€29.00` in English), while the CURRENCY belongs to the business and
 * is what will actually be charged. A Polish owner reading the German UI still
 * sees złoty.
 */
export function formatPlanPrice(minorUnits: number, currency: Currency, tag: string): string {
  return new Intl.NumberFormat(tag, {
    style: 'currency',
    currency,
    // Whole-euro/złoty prices: showing "29,00 zł" adds noise to a number whose
    // whole job is to be read at a glance. A price with real minor units still
    // renders them, because `maximumFractionDigits` stays at 2.
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
  }).format(minorUnits / 100);
}
